import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import EducationParametersSection from './EducationParametersSection';
import SavingsParametersSection from './SavingsParametersSection';
import ExpensesParametersSection from './ExpensesParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import StateSupportsSection from './StateSupportsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета накоплений на образование
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setSavingsData,
  educationLevels,
  universityTypes,
  universityLocations,
  savePlan 
}) => {
  
  // Состояние для имени сохраняемого плана
  const [savePlanName, setSavePlanName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем связанные поля в зависимости от типа образования
      if (name === 'educationLevel') {
        const selectedEducationType = educationLevels.find(type => type.id === fieldValue);
        if (selectedEducationType) {
          newState.educationDuration = selectedEducationType.years.toString();
          // Обновляем стоимость обучения если она была стандартной
          const currentTuition = parseFloatOrDefault(prev.tuitionFeePerYear, 0);
          const currentUniType = universityTypes.find(type => type.id === prev.universityType);
          const multiplier = currentUniType ? currentUniType.costMultiplier : 1;
          
          const avgCostPerEducation = [150000, 175000, 200000, 250000, 80000, 100000];
          const avgCost = avgCostPerEducation.includes(currentTuition / multiplier) ? 
            (selectedEducationType.avgCost * multiplier) : 
            currentTuition;

          newState.tuitionFeePerYear = avgCost.toString();
        }
      }

      // Обновляем стоимость обучения при изменении типа университета
      if (name === 'universityType') {
        const selectedUniType = universityTypes.find(type => type.id === fieldValue);
        const prevUniType = universityTypes.find(type => type.id === prev.universityType);
        
        if (selectedUniType && prevUniType) {
          const currentTuition = parseFloatOrDefault(prev.tuitionFeePerYear, 0);
          // Рассчитываем базовую стоимость и умножаем на новый множитель
          const baseCost = currentTuition / prevUniType.costMultiplier;
          newState.tuitionFeePerYear = (baseCost * selectedUniType.costMultiplier).toFixed(0);
        }
      }

      // Обновляем расходы на проживание при изменении местоположения
      if (name === 'universityLocation') {
        const selectedLocation = universityLocations.find(loc => loc.id === fieldValue);
        const prevLocation = universityLocations.find(loc => loc.id === prev.universityLocation);
        
        if (selectedLocation && prevLocation) {
          const currentLivingExpenses = parseFloatOrDefault(prev.livingExpensesPerYear, 0);
          // Рассчитываем базовую стоимость и умножаем на новый множитель
          const baseExpenses = currentLivingExpenses / prevLocation.livingExpensesMultiplier;
          newState.livingExpensesPerYear = (baseExpenses * selectedLocation.livingExpensesMultiplier).toFixed(0);
        }
      }

      // Сбрасываем связанные поля для материнского капитала
      if (name === 'includeMaternityCapital' && !checked) {
        newState.maternityCapitalAmount = '524500';
      }
      
      // Сбрасываем связанные поля для региональных программ
      if (name === 'includeRegionalPrograms' && !checked) {
        newState.regionalSupportAmount = '0';
      }
      
      // Сбрасываем связанные поля для дополнительных расходов
      if (name === 'includeExtraExpenses' && !checked) {
        newState.extraExpensesAmount = '50000';
        newState.extraExpensesDescription = 'Подготовительные курсы';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [educationLevels, universityTypes, universityLocations, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      yearsUntilStart, educationDuration, currentSavings, monthlyContribution,
      expectedReturn, inflationRate, tuitionFeePerYear, livingExpensesPerYear,
      booksAndSuppliesPerYear, scholarshipAmount, maternityCapitalAmount,
      regionalSupportAmount, extraExpensesAmount, educationLevel, universityType, universityLocation
    } = formData;

    // Проверка основных числовых параметров
    if (!isPositiveNumber(yearsUntilStart)) 
      newErrors.yearsUntilStart = 'Введите положительное число лет до начала обучения';
    
    if (!isPositiveNumber(educationDuration)) 
      newErrors.educationDuration = 'Введите положительное число лет обучения';
    
    if (!isNumber(currentSavings) || parseFloat(currentSavings) < 0) 
      newErrors.currentSavings = 'Введите неотрицательное значение текущих накоплений';
    
    if (!isPositiveNumber(monthlyContribution)) 
      newErrors.monthlyContribution = 'Введите положительное значение ежемесячного взноса';
    
    if (!isNumber(expectedReturn)) 
      newErrors.expectedReturn = 'Введите корректное значение ожидаемой доходности';
    
    if (!isNumber(inflationRate)) 
      newErrors.inflationRate = 'Введите корректное значение ожидаемой инфляции';
    
    if (parseFloatOrDefault(expectedReturn) < 0 || parseFloatOrDefault(expectedReturn) > 30)
      newErrors.expectedReturn = 'Доходность должна быть в диапазоне от 0% до 30%';
    
    if (parseFloatOrDefault(inflationRate) < 0 || parseFloatOrDefault(inflationRate) > 20)
      newErrors.inflationRate = 'Инфляция должна быть в диапазоне от 0% до 20%';

    // Проверка расходов
    if (!isPositiveNumber(tuitionFeePerYear)) 
      newErrors.tuitionFeePerYear = 'Введите положительное значение стоимости обучения';
    
    if (!isPositiveNumber(livingExpensesPerYear)) 
      newErrors.livingExpensesPerYear = 'Введите положительное значение расходов на проживание';
    
    if (!isNumber(booksAndSuppliesPerYear) || parseFloat(booksAndSuppliesPerYear) < 0) 
      newErrors.booksAndSuppliesPerYear = 'Введите неотрицательное значение расходов на учебники';
    
    if (!isNumber(scholarshipAmount) || parseFloat(scholarshipAmount) < 0) 
      newErrors.scholarshipAmount = 'Введите неотрицательное значение стипендии';

    // Проверка дополнительных параметров
    if (formData.includeMaternityCapital && (!isPositiveNumber(maternityCapitalAmount) || parseFloat(maternityCapitalAmount) > 1000000)) 
      newErrors.maternityCapitalAmount = 'Введите корректное значение материнского капитала (до 1,000,000 руб)';
    
    if (formData.includeRegionalPrograms && (!isNumber(regionalSupportAmount) || parseFloat(regionalSupportAmount) < 0)) 
      newErrors.regionalSupportAmount = 'Введите неотрицательное значение региональной поддержки';
    
    if (formData.includeExtraExpenses && (!isNumber(extraExpensesAmount) || parseFloat(extraExpensesAmount) < 0)) 
      newErrors.extraExpensesAmount = 'Введите неотрицательное значение дополнительных расходов';

    // Проверка выбора уровня образования, типа и местоположения университета
    if (!educationLevel) newErrors.educationLevel = 'Выберите уровень образования';
    if (!universityType) newErrors.universityType = 'Выберите тип учебного заведения';
    if (!universityLocation) newErrors.universityLocation = 'Выберите местоположение учебного заведения';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета накоплений и стоимости образования
  const calculateEducationSavings = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSavingsData([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const yearsUntilStart = parseIntOrDefault(formData.yearsUntilStart);
        const educationDuration = parseIntOrDefault(formData.educationDuration);
        const currentSavings = parseFloatOrDefault(formData.currentSavings);
        const monthlyContribution = parseFloatOrDefault(formData.monthlyContribution);
        const expectedAnnualReturn = parseFloatOrDefault(formData.expectedReturn) / 100;
        const annualInflationRate = parseFloatOrDefault(formData.inflationRate) / 100;
        const tuitionFeePerYear = parseFloatOrDefault(formData.tuitionFeePerYear);
        const livingExpensesPerYear = parseFloatOrDefault(formData.livingExpensesPerYear);
        const booksAndSuppliesPerYear = parseFloatOrDefault(formData.booksAndSuppliesPerYear);
        const scholarshipAmount = parseFloatOrDefault(formData.scholarshipAmount);
        const includeMaternityCapital = formData.includeMaternityCapital;
        const maternityCapitalAmount = includeMaternityCapital ? parseFloatOrDefault(formData.maternityCapitalAmount) : 0;
        const includeRegionalPrograms = formData.includeRegionalPrograms;
        const regionalSupportAmount = includeRegionalPrograms ? parseFloatOrDefault(formData.regionalSupportAmount) : 0;
        const includeTaxDeduction = formData.includeTaxDeduction;
        const includeExtraExpenses = formData.includeExtraExpenses;
        const extraExpensesAmount = includeExtraExpenses ? parseFloatOrDefault(formData.extraExpensesAmount) : 0;
        
        // --- Расчетные функции ---
        
        // Расчет будущей стоимости с учетом инфляции
        const calculateFutureValue = (presentValue, years) => {
          return presentValue * Math.pow(1 + annualInflationRate, years);
        };
        
        // Расчет налогового вычета
        const calculateTaxDeduction = (tuitionTotal) => {
          // В России налоговый вычет за обучение ограничен 120,000 руб в год (13% от этой суммы)
          const maxDeductionBase = 120000; 
          const taxRate = 0.13;
          const yearsToDeduct = Math.min(educationDuration, 5); // Макс. 5 лет стандартно для вычета
          
          let totalDeduction = 0;
          if (includeTaxDeduction) {
            const yearlyDeduction = Math.min(tuitionFeePerYear, maxDeductionBase) * taxRate;
            totalDeduction = yearlyDeduction * yearsToDeduct;
          }
          
          return totalDeduction;
        };
        
        // --- Выполняем расчеты ---
        
        // Расчет будущей стоимости обучения с учетом инфляции
        const futureTuitionPerYear = calculateFutureValue(tuitionFeePerYear, yearsUntilStart);
        const futureLivingExpensesPerYear = calculateFutureValue(livingExpensesPerYear, yearsUntilStart);
        const futureBooksAndSuppliesPerYear = calculateFutureValue(booksAndSuppliesPerYear, yearsUntilStart);
        const futureExtraExpensesTotal = includeExtraExpenses ? calculateFutureValue(extraExpensesAmount, yearsUntilStart) : 0;
        
        // Общая стоимость обучения
        const tuitionTotal = futureTuitionPerYear * educationDuration;
        const livingExpensesTotal = futureLivingExpensesPerYear * educationDuration;
        const booksAndSuppliesTotal = futureBooksAndSuppliesPerYear * educationDuration;
        const scholarshipTotal = scholarshipAmount * 12 * educationDuration; // Стипендия ежемесячная * кол-во месяцев
        
        // Расчет государственной поддержки и вычетов
        const maternityCapitalFutureValue = includeMaternityCapital ? 
          calculateFutureValue(maternityCapitalAmount, yearsUntilStart/2) : 0; // Предполагаем использование примерно в середине накопительного периода
        
        const regionalSupportFutureValue = includeRegionalPrograms ? 
          calculateFutureValue(regionalSupportAmount, yearsUntilStart/2) : 0;
        
        const taxDeductionTotal = calculateTaxDeduction(tuitionTotal);
        
        // Общая стоимость образования
        const totalEducationCost = tuitionTotal + livingExpensesTotal + booksAndSuppliesTotal + futureExtraExpensesTotal - scholarshipTotal;
        
        // Расчет необходимых накоплений с учетом поддержки и вычетов
        const netEducationCost = Math.max(0, totalEducationCost - maternityCapitalFutureValue - regionalSupportFutureValue - taxDeductionTotal);
        
        // --- Моделирование накоплений ---
        
        const monthlyReturnRate = Math.pow(1 + expectedAnnualReturn, 1/12) - 1;
        const totalMonths = yearsUntilStart * 12;
        
        let currentBalance = currentSavings;
        const savingsChart = [];
        
        // Поквартальное моделирование накоплений
        for (let month = 1; month <= totalMonths; month++) {
            // Ежемесячная прибыль от инвестиций
            const monthlyReturn = currentBalance * monthlyReturnRate;
            
            // Ежемесячное пополнение
            currentBalance += monthlyContribution + monthlyReturn;
            
            // Сохраняем данные для графика (поквартально для оптимизации)
            if (month % 3 === 0 || month === totalMonths) {
                savingsChart.push({
                    month,
                    year: Math.floor(month / 12),
                    monthLabel: `${Math.floor(month / 12)} лет ${month % 12} мес.`,
                    balance: currentBalance,
                    contribution: month * monthlyContribution,
                    returns: currentBalance - currentSavings - (month * monthlyContribution)
                });
            }
        }
        
        // Итоговая сумма накоплений
        const finalSavingsAmount = currentBalance;
        
        // Дефицит или профицит накоплений
        const savingsGap = finalSavingsAmount - netEducationCost;
        
        // Необходимые корректировки ежемесячных взносов
        let adjustedMonthlyContribution = 0;
        
        if (savingsGap < 0) {
            // Если накоплений недостаточно, рассчитываем необходимую корректировку
            // Решаем для ежемесячного платежа: FV = PMT * ((1+r)^n - 1) / r
            const targetAmount = netEducationCost;
            const pmt = (targetAmount - currentSavings * Math.pow(1 + monthlyReturnRate, totalMonths)) * 
                        monthlyReturnRate / 
                        (Math.pow(1 + monthlyReturnRate, totalMonths) - 1);
                        
            adjustedMonthlyContribution = Math.max(0, pmt);
        }
        
        // --- Подготовка результатов расчета ---
        const results = {
            yearsUntilStart,
            educationDuration,
            totalEducationCost,
            
            // Детали стоимости обучения
            tuitionCost: tuitionTotal,
            livingExpensesCost: livingExpensesTotal,
            booksAndSuppliesCost: booksAndSuppliesTotal,
            extraExpensesCost: futureExtraExpensesTotal,
            scholarshipBenefit: scholarshipTotal,
            
            // Государственная поддержка и вычеты
            maternityCapitalBenefit: maternityCapitalFutureValue,
            regionalSupportBenefit: regionalSupportFutureValue,
            taxDeductionBenefit: taxDeductionTotal,
            
            // Итоговые расчеты
            netEducationCost,
            finalSavingsAmount,
            savingsGap,
            
            // Текущие параметры накоплений
            currentSavings,
            monthlyContribution,
            totalContributions: monthlyContribution * totalMonths,
            expectedReturns: finalSavingsAmount - currentSavings - (monthlyContribution * totalMonths),
            
            // Рекомендации
            isEnough: savingsGap >= 0,
            adjustedMonthlyContribution: adjustedMonthlyContribution,
            
            // Инфляционные значения
            annualInflationRate,
            currentTuitionPerYear: tuitionFeePerYear,
            futureTuitionPerYear,
            
            // Данные для графика
            savingsDataChart: savingsChart
        };
        
        setResults(results);
        setSavingsData(savingsChart);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSavingsData([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSavingsData, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      educationLevel: 'bachelor',
      yearsUntilStart: '5',
      educationDuration: '4',
      currentSavings: '100000',
      monthlyContribution: '5000',
      expectedReturn: '5',
      inflationRate: '4',
      tuitionFeePerYear: '150000',
      livingExpensesPerYear: '120000',
      booksAndSuppliesPerYear: '25000',
      scholarshipAmount: '0',
      includeMaternityCapital: false,
      maternityCapitalAmount: '524500',
      includeRegionalPrograms: false,
      regionalSupportAmount: '0',
      includeTaxDeduction: false,
      includeExtraExpenses: false,
      extraExpensesAmount: '50000',
      extraExpensesDescription: 'Подготовительные курсы',
      universityLocation: 'hometown',
      universityType: 'public'
    });
    setResults(null);
    setSavingsData([]);
    setErrors({});
    setSavePlanName('');
    setShowSaveDialog(false);
  };

  // Экспорт плана в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Год', 'Месяц', 'Баланс накоплений', 'Накопленные взносы', 'Инвестиционный доход'];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = results.savingsDataChart.map(item => {
      return [
        Math.floor(item.month / 12),
        item.month % 12,
        formatCsvNumber(item.balance),
        formatCsvNumber(item.contribution),
        formatCsvNumber(item.returns)
      ].join(';');
    });

    // Добавим основную информацию в начало файла
    const infoRows = [
      ['План накоплений на образование'],
      [''],
      ['Уровень образования:', educationLevels.find(e => e.id === formData.educationLevel)?.name || formData.educationLevel],
      ['Лет до начала обучения:', formData.yearsUntilStart],
      ['Длительность обучения (лет):', formData.educationDuration],
      [''],
      ['Текущие накопления:', formatCsvNumber(results.currentSavings) + ' руб.'],
      ['Ежемесячный взнос:', formatCsvNumber(results.monthlyContribution) + ' руб.'],
      ['Ожидаемая доходность:', formData.expectedReturn + '% годовых'],
      ['Ожидаемая инфляция:', formData.inflationRate + '% годовых'],
      [''],
      ['Стоимость обучения (сейчас):', formatCsvNumber(results.currentTuitionPerYear) + ' руб./год'],
      ['Стоимость обучения (будущая):', formatCsvNumber(results.futureTuitionPerYear) + ' руб./год'],
      [''],
      ['Итоговая стоимость образования:', formatCsvNumber(results.totalEducationCost) + ' руб.'],
      ['Финальная сумма накоплений:', formatCsvNumber(results.finalSavingsAmount) + ' руб.'],
      ['Профицит/дефицит накоплений:', formatCsvNumber(results.savingsGap) + ' руб.'],
      [''],
      ['Данные по накоплениям по месяцам:'],
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'план_накоплений_на_образование.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения плана
  const handleSavePlan = () => {
    if (showSaveDialog) {
      const savedResult = savePlan(savePlanName || null);
      if (savedResult) {
        alert(`План "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSavePlanName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateEducationSavings();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calculator className="w-6 h-6 text-blue-500" />
          Параметры расчета накоплений
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры образования */}
        <EducationParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          educationLevels={educationLevels}
          universityTypes={universityTypes}
          universityLocations={universityLocations}
        />

        {/* Параметры накоплений */}
        <SavingsParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Параметры расходов */}
        <ExpensesParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Государственная поддержка */}
        <StateSupportsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения плана */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={savePlanName}
                onChange={(e) => setSavePlanName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSavePlan}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать накопления'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать план (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSavePlan}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить план
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;